/**
 * The per-user "add to site" pipeline, shared by Workforce (+ Add user,
 * Add several) and Members (Invite user, Invite several). One place so the
 * two entry points can never drift apart again — the Members page used to
 * send a WorkOS-only invite that created a login and no roster row, and
 * coordinators then had to "add" the same person a second time.
 *
 * Three writes, in order; returns WHAT happened instead of toasting so a
 * bulk table can show one status per row. Never throws for the expected
 * failures (reducer refusal, org grant refusal, invite error).
 *
 * Step 1 — minetwin `add_user`: the pending_user / user_site row with the
 * occupation. Orientation assignment is intentionally NOT done here
 * (Cloud Lead + Samuel, 2026-06-17): server-side `auto_assign`
 * orientations enroll the user transparently when their occupation
 * matches; explicit assignment lives on the Orientation Assignment page.
 *
 * Step 1b — org-side project membership. Per SERVER-MODULE-MEMBERSHIP-
 * PROPAGATION-PLAN Phase 0 (2026-07-10 org side landed), calling
 * `invite_project_user_by_email(..., autoGrant: true)` is sufficient:
 *   - Fresh email → Pending invitation row; org's user_connected drains
 *     it via `claim_and_fill_email_from_jwt` using the JWT email claim.
 *   - Existing admin_user → autoGrant=true grants project_user directly.
 * "already pending" / "already a member" mean the target state is already
 * achieved and are tolerated. Email is trimmed + lowercased to match the
 * org's storage convention so the JWT-time claim lookup finds the row.
 *
 * Step 2 — the WorkOS invite via the SWA-co-located Function (shared
 * with the Resend invite row action).
 */
import { useSpacetimeDB } from 'spacetimedb/react';
import type { DbConnection as OrgConn } from '@sdk/org';
import type { DbConnection as MinetwinConn } from '@sdk/minetwin';
import { Identity } from 'spacetimedb';
import { useAuthKit } from '@/contexts/AuthProvider';
import { sendWorkosInvite } from '@/services/workosInvite';
import { awaitProcedureJson } from '@/utils/awaitProcedure';
import type { RowOutcome } from '@/lib/bulkAddUsers';
import {
  classifyAlreadyRefusal,
  findMemberByEmail,
  fromProjectRoleTag,
  roleChangeOnReAdd,
  toProjectRoleTag,
  type ProjectUserJson,
  type SiteRole,
} from '@/lib/siteRole';
import type { Site } from '@/data/types';

export type AddSiteUserInput = {
  email: string;
  occupation: string;
  /** minetwin project id (the site's `projectName`). */
  projectId: string;
  /** Role on this site's project (#241). Defaults to Reader. */
  siteRole?: SiteRole;
  /** WorkOS org role for a NEW login; the Function defaults to `member`.
   *  `admin` makes them OrgAdmin of EVERY site, so only the Members page
   *  offers it, behind an explicit checkbox. */
  roleSlug?: string;
  /** "Allow authoring" for a Reader (ORIENTATION-ROLES P1): set through
   *  `set_site_authoring` after the add. Ignored for Writer and Admin. */
  allowAuthoring?: boolean;
};

/**
 * The invite Function only exists in the deployed SWA's api/ folder, NOT
 * in `npm run dev`. In dev we default to skipping the fetch entirely so
 * local devs see clean toasts with no scary console 503/404 from a
 * missing endpoint. Devs who want to test the local invite flow set
 * `VITE_ENABLE_LOCAL_INVITES=true` in .env.local AND run `func start`
 * in api/ alongside the portal dev server.
 */
export const inviteEnabled =
  import.meta.env.PROD || import.meta.env.VITE_ENABLE_LOCAL_INVITES === 'true';

export function useAddSiteUser(currentSite: Site | undefined): {
  addOneUser: (input: AddSiteUserInput) => Promise<RowOutcome>;
  inviteEnabled: boolean;
} {
  // Add user writes to two modules: minetwin (workforce row + occupation)
  // and org (project_user via invite_project_user_by_email with autoGrant).
  // Without the org write the invitee has no `project_user` row on first
  // sign-in and lands on an empty Sites list.
  const minetwinState = useSpacetimeDB('minetwin');
  const orgState = useSpacetimeDB('org');
  const { organizationId, getAccessToken } = useAuthKit();

  async function addOneUser(input: AddSiteUserInput): Promise<RowOutcome> {
    const email = input.email.trim().toLowerCase();
    const conn = minetwinState.getConnection() as MinetwinConn | null;
    if (!conn) {
      return { kind: 'failed', note: 'Not connected to MineTwin module' };
    }
    try {
      await conn.reducers.addUser({
        email,
        occupation: input.occupation,
        projectId: input.projectId,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { kind: 'failed', note: message.slice(0, 200) };
    }

    let grantNote = '';
    let roleNote = '';
    const siteRole: SiteRole = input.siteRole ?? 'Reader';
    // The flag lives in its own module table and composes with membership
    // propagation in either order, so it can go before the org grant.
    if (input.allowAuthoring && siteRole === 'Reader') {
      try {
        await conn.reducers.setSiteAuthoring({ email, projectId: input.projectId, allow: true });
        roleNote = 'Allow authoring on';
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        roleNote = `Allow authoring not set: ${message.slice(0, 120)}`;
      }
    }
    const orgConn = orgState.getConnection() as OrgConn | null;
    // Only a numeric org project id: a site known only from admin_site_ids
    // carries its project name as id (lib/siteList), which parseInt could
    // misread ("2024-north" as 2024).
    const orgProjectId = currentSite && /^\d+$/.test(currentSite.id)
      ? Number(currentSite.id)
      : Number.NaN;
    if (orgConn && Number.isFinite(orgProjectId)) {
      try {
        await orgConn.reducers.inviteProjectUserByEmail({
          projectId: orgProjectId,
          email,
          role: { tag: toProjectRoleTag(siteRole) },
          autoGrant: true,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        const already = classifyAlreadyRefusal(message);
        if (already === 'member') {
          // The org module keeps an existing member's role on a re-invite, so
          // a chosen role that is higher has to be applied separately.
          roleNote = joinNotes(roleNote, await raiseExistingRole(orgConn, orgProjectId, email, siteRole));
        } else if (already === 'pending') {
          roleNote = joinNotes(roleNote, 'already invited to this site; the role from that first invite stands');
        } else {
          grantNote = `in the workforce but got no site access and needs a re-add: ${message.slice(0, 120)}`;
        }
      }
    }

    if (!inviteEnabled) {
      return {
        kind: 'added',
        note: joinNotes(
          grantNote || 'invite handled by the deployed SWA only (set VITE_ENABLE_LOCAL_INVITES=true to test locally)',
          roleNote,
        ),
      };
    }
    const invite = await sendWorkosInvite({
      email,
      organizationId,
      roleSlug: input.roleSlug,
      getAccessToken,
      dev: import.meta.env.DEV,
    });
    if (grantNote) return { kind: 'invite-failed', note: grantNote };
    if (!invite.ok) return { kind: 'invite-failed', note: joinNotes(invite.sub, roleNote) };
    return { kind: invite.status === 'sent' ? 'invited' : 'added', note: joinNotes(invite.sub, roleNote) };
  }

  return { addOneUser, inviteEnabled };
}

function joinNotes(...notes: string[]): string {
  return notes.filter(Boolean).join(' · ');
}

/**
 * For someone already on the site's project: raise their role to `chosen` if
 * that is higher (never lower it), and say what happened. Finds them through
 * `admin_list_project_users` by username = email; anything it cannot do is
 * reported in the note, not thrown.
 */
async function raiseExistingRole(
  orgConn: OrgConn,
  projectId: number,
  email: string,
  chosen: SiteRole,
): Promise<string> {
  let users: ProjectUserJson[];
  try {
    const data = await awaitProcedureJson<{ users: ProjectUserJson[] }>(
      orgConn,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated procedures accessor
      (c: any) => c.procedures.adminListProjectUsers({ projectId }),
    );
    users = data.users ?? [];
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return `already on this site; could not check their role (${message.slice(0, 80)})`;
  }
  const member = findMemberByEmail(users, email);
  const current = member ? fromProjectRoleTag(member.role) : null;
  if (!member || !current) {
    return `already on this site; their role was not changed (set it in TurboTwin › Users if it should be ${chosen})`;
  }
  const next = roleChangeOnReAdd(current, chosen);
  if (!next) return `already on this site as ${current}; role kept`;
  try {
    await orgConn.reducers.updateProjectUserRole({
      projectId,
      userIdentity: Identity.fromString(member.user_identity),
      newRole: { tag: toProjectRoleTag(next) },
    });
    return `already on this site; role raised from ${current} to ${next}`;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return `already on this site as ${current}; could not raise to ${next} (${message.slice(0, 80)})`;
  }
}
